import CryptoCheckout from "features/settings/Subscription/CryptoCheckout.tsx";
import PaymentMethodsCheckout from "features/settings/Subscription/PaymentMethodsCheckout.tsx";
import React, { useContext, useState } from "react";
import Text from "common/Typography/Text";
import { Divider } from "common/styles.ts";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import { LanguageNameSpaces } from "@shared/types";
import { DURATION_TO_SECONDARY_DISPLAY } from "@shared/features/settings/subscription/constants";
import useSummary from "features/settings/Billing/ApiSlots/useSummary.ts";
import PaypalCheckout from "features/settings/Subscription/PaypalCheckout.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import styled from "styled-components";
import { toFixed } from "@shared/utils/numbers.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";

const Summary = () => {
  const {
    isAutoRenewal,
    numApiSlots,
    upgradePrices,
    isUpgradePricesLoading,
    duration,
  } = useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const {
    onGetPaymentLink,
    form,
    isCrypto,
    isPaypal,
    email,
    setEmail,
    getPaymentLinkStatus,
    baseRequest,
    hasMinSlotsError,
    isInvalidStep,
  } = useSummary();
  const [acceptedCryptoTerms, setAcceptedCryptoTerms] = useState(false);

  const autoRenewalInfo = isAutoRenewal && !isInvalidStep && (
    <AutoRenewalInfo>
      <Text>
        {t("api_slots.auto_renewal_info", {
          price: upgradePrices
            ? toFixed(
                upgradePrices.slots_recurring_price +
                  upgradePrices.plan_recurring_price,
                2
              )
            : "",
          slots: upgradePrices?.total_number_of_clients ?? "",
          duration: t(DURATION_TO_SECONDARY_DISPLAY[duration])?.toLowerCase(),
        })}
      </Text>
    </AutoRenewalInfo>
  );

  const isPayDisabled = !numApiSlots || hasMinSlotsError || isInvalidStep;

  const getCheckoutContent = () => {
    if (isCrypto) {
      return (
        <CryptoCheckout
          form={form}
          email={email}
          setEmail={setEmail}
          getPaymentLink={onGetPaymentLink}
          getPaymentLinkStatus={getPaymentLinkStatus}
          expirationText={autoRenewalInfo}
          baseRequest={baseRequest}
          acceptedTerms={acceptedCryptoTerms}
          setAcceptedTerms={setAcceptedCryptoTerms}
        />
      );
    } else if (isPaypal) {
      return (
        <PaypalCheckout
          form={form}
          setEmail={setEmail}
          getPaymentLink={onGetPaymentLink}
          loadingPaymentLink={getPaymentLinkStatus.isLoading}
          disabled={isPayDisabled}
          buyApiSlotsContent={
            <>
              {autoRenewalInfo}
              <Divider />
              <Flex style={{ marginBottom: 12 }}>
                <Title level={3}>{t("api_slots.total")}</Title>
                <Title level={3}>
                  ${toFixed(upgradePrices?.slots_setup_price ?? 0, 2)}
                </Title>
              </Flex>
            </>
          }
        />
      );
    }
  };

  return (
    <PaymentMethodsCheckout
      form={form}
      checkoutContent={
        <LoadingSpinnerOverlay isLoading={isUpgradePricesLoading} wrap={true}>
          {getCheckoutContent()}
        </LoadingSpinnerOverlay>
      }
      hideCreditCard={true}
    />
  );
};

export default Summary;

const AutoRenewalInfo = styled.div`
  margin-top: 8px;
  position: relative;
  min-height: 44px;
`;
